/**
 * Typography configuration for Dataloop Asset Management
 *
 * Uses Roboto font family with proper weights and sizes
 * Follows Material Design typography guidelines
 */

import { TypographyVariantsOptions } from '@mui/material/styles';
import { semanticColors } from './colors';

/**
 * Roboto font family with system fallbacks
 */
export const fontFamily = [
	'Roboto',
	'-apple-system',
	'BlinkMacSystemFont',
	'"Segoe UI"',
	'"Helvetica Neue"',
	'Arial',
	'sans-serif',
	'"Apple Color Emoji"',
	'"Segoe UI Emoji"',
	'"Segoe UI Symbol"',
].join(',');

/**
 * Typography configuration for Material-UI theme
 */
export const typography: TypographyVariantsOptions = {
	fontFamily,

	h1: {
		fontFamily,
		fontSize: '2.5rem', // 40px
		fontWeight: 500,
		lineHeight: 1.2,
		letterSpacing: '-0.01562em',
		color: semanticColors.textPrimary,
	},
	h2: {
		fontFamily,
		fontSize: '2rem', // 32px
		fontWeight: 500,
		lineHeight: 1.25,
		letterSpacing: '-0.00833em',
		color: semanticColors.textPrimary,
	},
	h3: {
		fontFamily,
		fontSize: '1.75rem', // 28px
		fontWeight: 500,
		lineHeight: 1.3,
		letterSpacing: '0em',
		color: semanticColors.textPrimary,
	},
	h4: {
		fontFamily,
		fontSize: '1.5rem', // 24px
		fontWeight: 500,
		lineHeight: 1.35,
		letterSpacing: '0.00735em',
		color: semanticColors.textPrimary,
	},
	h5: {
		fontFamily,
		fontSize: '1.25rem', // 20px
		fontWeight: 500,
		lineHeight: 1.4,
		letterSpacing: '0em',
		color: semanticColors.textPrimary,
	},
	h6: {
		fontFamily,
		fontSize: '1rem', // 16px
		fontWeight: 500,
		lineHeight: 1.5,
		letterSpacing: '0.0075em',
		color: semanticColors.textPrimary,
	},

	body1: {
		fontFamily,
		fontSize: '1rem', // 16px
		fontWeight: 400,
		lineHeight: 1.5,
		letterSpacing: '0.00938em',
		color: semanticColors.textPrimary,
	},
	body2: {
		fontFamily,
		fontSize: '0.875rem', // 14px
		fontWeight: 400,
		lineHeight: 1.43,
		letterSpacing: '0.01071em',
		color: semanticColors.textSecondary,
	},

	subtitle1: {
		fontFamily,
		fontSize: '1rem', // 16px
		fontWeight: 500,
		lineHeight: 1.75,
		letterSpacing: '0.00938em',
		color: semanticColors.textPrimary,
	},
	subtitle2: {
		fontFamily,
		fontSize: '0.875rem', // 14px
		fontWeight: 500,
		lineHeight: 1.57,
		letterSpacing: '0.00714em',
		color: semanticColors.textSecondary,
	},

	button: {
		fontFamily,
		fontSize: '0.875rem', // 14px
		fontWeight: 500,
		lineHeight: 1.75,
		letterSpacing: '0.02857em',
		textTransform: 'none', // Disable uppercase
	},

	caption: {
		fontFamily,
		fontSize: '0.75rem', // 12px
		fontWeight: 400,
		lineHeight: 1.66,
		letterSpacing: '0.03333em',
		color: semanticColors.textSecondary,
	},

	// Overline
	overline: {
		fontFamily,
		fontSize: '0.75rem', // 12px
		fontWeight: 500,
		lineHeight: 2.66,
		letterSpacing: '0.08333em',
		textTransform: 'uppercase',
		color: semanticColors.textSecondary,
	},
};

export const fontWeights = {
	light: 300,
	regular: 400,
	medium: 500,
	bold: 700,
} as const;

/**
 * Font sizes (in rem)
 */
export const fontSizes = {
	xs: '0.75rem', // 12px
	sm: '0.875rem', // 14px
	base: '1rem', // 16px
	lg: '1.125rem', // 18px
	xl: '1.25rem', // 20px
	'2xl': '1.5rem', // 24px
	'3xl': '1.75rem', // 28px
	'4xl': '2rem', // 32px
	'5xl': '2.5rem', // 40px
} as const;
